/**
 * @fileoverview useBankBalances - Monthly bank balance management hook.
 *
 * Manages monthly bank account balances including:
 * - Fetching balances for a specific month
 * - Auto-populating from previous month if no data exists
 * - Updating balances for the current month
 * - Deleting balance entries for the current month
 *
 * @module hooks/useBankBalances
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Hook for managing monthly bank balances.
 *
 * @param {number} budgetKey - Budget key
 * @param {Date} selectedMonth - Currently selected month
 * @returns {Object} Bank balance state and operations
 */
export const useBankBalances = (budgetKey, selectedMonth) => {
    const { getAccessTokenSilently } = useAuth0();
    const [balances, setBalances] = useState([]);
    const [totalBalance, setTotalBalance] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

    /**
     * Format month for API (YYYY-MM)
     */
    const formatMonth = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    /**
     * Fetch bank balances for the selected month
     */
    const fetchBalances = useCallback(async () => {
        if (!budgetKey || !selectedMonth) return;

        try {
            setLoading(true);
            setError(null);
            const monthKey = formatMonth(selectedMonth);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/months/${monthKey}/bank-balances`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch bank balances');
            }

            const data = await response.json();
            setBalances(data.balances || []);
            setTotalBalance(data.totalBalance || 0);
        } catch (err) {
            setError(err.message);
            // eslint-disable-next-line no-console
            console.error('Fetch bank balances error:', err);
        } finally {
            setLoading(false);
        }
    }, [budgetKey, selectedMonth, getAccessTokenSilently, API_BASE_URL]);

    /**
     * Update bank balances for the selected month
     */
    const updateBalances = async (updatedBalances) => {
        try {
            setError(null);
            const monthKey = formatMonth(selectedMonth);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/months/${monthKey}/bank-balances`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ balances: updatedBalances })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update bank balances');
            }

            // Refresh balances after successful update
            await fetchBalances();
            return { success: true };
        } catch (err) {
            setError(err.message);
            // eslint-disable-next-line no-console
            console.error('Update bank balances error:', err);
            return { success: false, error: err.message };
        }
    };

    /**
     * Update a single balance in the local state (for real-time UI updates)
     * If the account doesn't exist in balances yet, add it
     */
    const updateLocalBalance = (accountName, newBalance) => {
        setBalances(prev => {
            const existingIndex = prev.findIndex(bal => bal.accountName === accountName);
            let updated;

            if (existingIndex >= 0) {
                // Update existing balance
                updated = prev.map(bal =>
                    bal.accountName === accountName
                        ? { ...bal, balance: parseFloat(newBalance) || 0 }
                        : bal
                );
            } else {
                // Add new balance entry
                updated = [
                    ...prev,
                    {
                        accountName,
                        balance: parseFloat(newBalance) || 0,
                        fromPreviousMonth: false
                    }
                ];
            }

            // Calculate total: positive balances minus negative balances
            const newTotal = updated.reduce((sum, bal) => {
                return sum + (bal.treatAsNegative ? -bal.balance : bal.balance);
            }, 0);
            setTotalBalance(newTotal);
            return updated;
        });
    };

    /**
     * Delete a bank balance entry for the selected month
     */
    const deleteBalance = async (accountName) => {
        try {
            setError(null);
            const monthKey = formatMonth(selectedMonth);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE_URL}/budgets/${budgetKey}/months/${monthKey}/bank-balances/${encodeURIComponent(accountName)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete bank balance');
            }

            // Refresh balances after successful delete
            await fetchBalances();
            return { success: true };
        } catch (err) {
            setError(err.message);
            // eslint-disable-next-line no-console
            console.error('Delete bank balance error:', err);
            return { success: false, error: err.message };
        }
    };

    // Fetch balances when budgetKey or selectedMonth changes
    useEffect(() => {
        fetchBalances();
    }, [fetchBalances]);

    return {
        balances,
        totalBalance,
        loading,
        error,
        fetchBalances,
        updateBalances,
        updateLocalBalance,
        deleteBalance
    };
};
